import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, RotateCcw, Volume2, VolumeX, ArrowRight } from "lucide-react";

const SCENES = [
  { img: "/brand/scene1.jpg", dur: 5.9, vo: "/media/vo1.mp3", kb: "lkb-a",
    kicker: "SINGAPORE · YEAR OF CLIMATE ADAPTATION 2026", title: "Sustainability is now business survival." },
  { img: "/brand/scene2.jpg", dur: 8.4, vo: "/media/vo2.mp3", kb: "lkb-b",
    kicker: "MEASURE · COMPLY · DE-RISK · DECARBONISE · FUND", title: "One AI platform. End to end." },
  { img: "/brand/scene3.jpg", dur: 5.4, vo: "/media/vo3.mp3", kb: "lkb-a", closing: true,
    kicker: "LESTARI · Sustainability AI Platform by SusTek", title: "Don't just report. Be ready." },
];

export function PitchVideo() {
  const [state, setState] = useState("poster"); // poster | playing | done
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioRefs = useRef([]);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  const stopAudio = () => audioRefs.current.forEach((a) => { if (a) { a.pause(); a.currentTime = 0; } });

  const playScene = (i) => {
    setIdx(i);
    const a = audioRefs.current[i];
    if (a) { a.muted = muted; a.currentTime = 0; a.play().catch(() => {}); }
    timerRef.current = setTimeout(() => {
      if (i + 1 < SCENES.length) playScene(i + 1);
      else setState("done");
    }, SCENES[i].dur * 1000);
  };

  const start = () => { clearTimeout(timerRef.current); stopAudio(); setState("playing"); playScene(0); };
  useEffect(() => () => { clearTimeout(timerRef.current); stopAudio(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { audioRefs.current.forEach((a) => { if (a) a.muted = muted; }); }, [muted]);

  const s = SCENES[idx];
  const showFrame = state !== "poster";

  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl ring-1 ring-[#0F3D3A]/20 bg-[#0F3D3A] select-none" data-testid="pitch-video">
      <style>{`
        @keyframes lkbA { from { transform: scale(1.02) translate(0,0); } to { transform: scale(1.14) translate(-1.6%,-1.2%); } }
        @keyframes lkbB { from { transform: scale(1.14) translate(1.4%,1.2%); } to { transform: scale(1.02) translate(0,0); } }
        .lkb-a { animation: lkbA linear forwards; }
        .lkb-b { animation: lkbB linear forwards; }
        @keyframes lTitleIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes lFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lFill { from { width: 0%; } to { width: 100%; } }
      `}</style>

      {SCENES.map((sc, i) => (
        <audio key={sc.vo} ref={(el) => { audioRefs.current[i] = el; }} src={sc.vo} preload="auto" />
      ))}

      {state === "poster" && (
        <button onClick={start} className="absolute inset-0 w-full h-full group cursor-pointer" data-testid="pitch-play-btn" aria-label="Play the Lestari explainer video">
          <img src="/brand/scene1.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F3D3A] via-[#0F3D3A]/70 to-[#0F3D3A]/30" />
          <div className="relative h-full flex flex-col items-center justify-center gap-4 px-6">
            <img src="/brand/logo_horizontal.png" alt="Lestari" className="h-10 sm:h-14 object-contain brightness-0 invert opacity-95" />
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-[#D4A84C] flex items-center justify-center shadow-xl transition-transform group-hover:scale-110">
              <Play className="h-7 w-7 sm:h-9 sm:w-9 text-[#0F3D3A] ml-1" fill="#0F3D3A" />
            </div>
            <div className="text-[#F2F4F3] text-xs sm:text-sm font-medium tracking-wide">Watch the explainer · with sound</div>
          </div>
        </button>
      )}

      {showFrame && (
        <>
          <img key={`img-${idx}-${state}`} src={s.img} alt="" style={{ animationDuration: `${s.dur + 1}s` }}
            className={`absolute inset-0 w-full h-full object-cover ${s.kb}`} />
          <div className={`absolute inset-0 ${s.closing ? "bg-[#0F3D3A]/80" : "bg-gradient-to-t from-[#0F3D3A]/85 via-[#0F3D3A]/25 to-transparent"}`} />

          {!s.closing ? (
            <div key={`t-${idx}`} className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
              <div className="text-[#D4A84C] font-mono text-[9px] sm:text-[11px] tracking-[0.22em] mb-2" style={{ animation: "lFadeIn .6s .2s both" }}>{s.kicker}</div>
              <div className="text-[#F2F4F3] font-bold text-xl sm:text-3xl lg:text-4xl tracking-tight" style={{ animation: "lTitleIn .7s .35s both", fontFamily: "Georgia, 'Times New Roman', serif" }}>
                {s.title}
              </div>
            </div>
          ) : (
            <div key={`t-${idx}`} className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <img src="/brand/logo_primary.png" alt="Lestari — Sustainability, Intelligence, Impact" className="h-16 sm:h-24 object-contain brightness-0 invert" style={{ animation: "lTitleIn .8s .1s both" }} />
              <div className="text-[#D4A84C] font-mono text-[10px] sm:text-xs tracking-[0.22em]" style={{ animation: "lFadeIn .6s .6s both" }}>{s.kicker}</div>
              <div className="text-[#F2F4F3] font-semibold text-base sm:text-2xl tracking-tight" style={{ animation: "lTitleIn .7s .8s both", fontFamily: "Georgia, serif" }}>
                {s.title}
              </div>
              <button onClick={() => navigate("/get-started")} data-testid="pitch-cta-btn"
                className="mt-2 inline-flex items-center gap-2 bg-[#D4A84C] hover:bg-[#c49a3f] text-[#0F3D3A] font-bold text-xs sm:text-sm tracking-wide px-5 py-2.5 rounded-full transition-colors shadow-lg"
                style={{ animation: "lTitleIn .7s 1.2s both" }}>
                BUILD YOUR CLIMATE-READY BUSINESS <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="absolute top-0 inset-x-0 flex gap-1 p-2.5">
            {SCENES.map((sc, i) => (
              <div key={i} className="h-[3px] flex-1 rounded-full bg-white/25 overflow-hidden">
                <div className="h-full bg-[#D4A84C]" style={
                  i < idx || state === "done" ? { width: "100%" }
                    : i === idx ? { animation: `lFill ${sc.dur}s linear forwards` } : { width: "0%" }} />
              </div>
            ))}
          </div>

          <div className="absolute bottom-2.5 right-2.5 flex gap-1.5">
            <button onClick={() => setMuted((m) => !m)} data-testid="pitch-mute-btn" aria-label={muted ? "Unmute" : "Mute"}
              className="h-8 w-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors">
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <button onClick={start} data-testid="pitch-replay-btn" aria-label="Replay"
              className="h-8 w-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white transition-colors">
              <RotateCcw className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
